import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  collection,
  doc,
  documentId,
  DocumentData,
  Firestore,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  runTransaction,
  serverTimestamp,
  startAfter,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from '@firebase/firestore';
import { from, map, Observable, of } from 'rxjs';
import { environment } from 'shared/environments/environment';
import { AuthService } from './auth.service';
import { getPeechFirestore } from './peech-firestore';

// Mirrors the iOS page size (FirestoreLibraryDataSource.swift).
const PAGE_SIZE = 25;

// Firestore caps 'in' queries at 30 values.
const IDS_CHUNK_SIZE = 30;

// Firestore's own hard cap on operations per writeBatch. iOS chunks its equivalent batch
// writes (FirestoreLibraryDataSource.applyBatchUpdate) at 250; the web selection UI has no
// such iOS counterpart to match, so this uses Firestore's actual ceiling instead.
const WRITE_BATCH_CHUNK_SIZE = 500;

// The libraryState values as stored in Firestore; 'all' is a UI filter, never stored.
export type LibraryState = 'notStarted' | 'inProgress' | 'completed';
export type LibraryFilter = LibraryState | 'all';

// iOS LibraryState is Comparable in this order; progress writes only ever move up it.
const LIBRARY_STATE_RANK: Record<LibraryState, number> = {
  notStarted: 0,
  inProgress: 1,
  completed: 2,
};

export interface ReadingProgressUpdate {
  readingItemPartIndex?: number;
  isFullyRead?: boolean;
  libraryState?: LibraryState;
}

// Outcome of a chunked bulk write (see batchUpdate): each writeBatch chunk commits
// atomically, so every id lands in exactly one of the two lists. `error` is the first
// chunk failure (for logging); null when every chunk committed.
export interface BulkWriteResult {
  committed: string[];
  failed: string[];
  error: unknown;
}

export type LibraryItemStatus = 'processing' | 'success' | 'failure';

// Mirrors iOS OutputType.swift — the backend-set shape a badge is computed from (never a
// URL-host or item-type check). `podcast`/`quiz` are decoded but currently unbadged on web.
export type LibraryOutputTypeCode = 'summary' | 'youtubeSummary' | 'podcast' | 'quiz';

export interface LibraryOutputType {
  code: LibraryOutputTypeCode;
}

// One TOC entry (users/{uuid}/items doc, tableOfContents field, iOS TableOfContentsItem):
// partIndex is partRange.from — inclusive, ZERO-based (BAC-522); null = entry is not
// navigable (partRange not backfilled, BAC-523).
export interface TableOfContentsEntry {
  label: string;
  partIndex: number | null;
  children: TableOfContentsEntry[];
}

export type LibraryItemType =
  | 'image'
  | 'pdf'
  | 'epub'
  | 'rtf'
  | 'docx'
  | 'pptx'
  | 'text'
  | 'web'
  | 'email'
  | 'kindle';

export interface LibraryItem {
  id: string;
  status: LibraryItemStatus;
  type: LibraryItemType;
  title: string | null;
  authors: string[];
  createdAt: Date | null;
  updatedAt: Date | null;
  logoUrl: string | null;
  durationSeconds: number | null;
  partsCount: number | null;
  language: string | null;
  url: string | null;
  domainName: string | null;
  contentUrl: string | null;
  outputType: LibraryOutputType | null;
  tableOfContents: TableOfContentsEntry[];
  listeningPartIndex: number | null;
  readingItemPartIndex: number | null;
  currentVoicePresetId: string | null;
  isFullyRead: boolean;
  isDeleted: boolean;
  // Set alongside isDeleted (soft delete): when the backend will purge the tombstone.
  purgeAt: Date | null;
  libraryState: LibraryState;
}

// Opaque to callers — keeps @firebase/firestore out of the components.
export type LibraryCursor = QueryDocumentSnapshot;

export interface LibraryPage {
  items: LibraryItem[];
  // Pass back into getItems to fetch the next page; null when this page is the last one.
  cursor: LibraryCursor | null;
}

// Read-only v1 of the library: same collection, filters, ordering and page size as the iOS
// app (users/{peechUserId}/items in the named DB), one-shot reads instead of listeners.
// peechUserId is the uuid from GET /users/details, NOT the Firebase uid.
@Injectable({
  providedIn: 'root',
})
export class LibraryService {
  private readonly firestore: Firestore;

  constructor(
    auth: AuthService,
    private http: HttpClient
  ) {
    this.firestore = getPeechFirestore(auth.app);
  }

  getItems(
    peechUserId: string,
    filter: LibraryFilter,
    cursor: LibraryCursor | null = null
  ): Observable<LibraryPage> {
    const constraints: QueryConstraint[] = [where('isDeleted', '==', false)];

    if (filter !== 'all') {
      constraints.push(where('libraryState', '==', filter));
    }

    constraints.push(orderBy('updatedAt', 'desc'));

    if (cursor) {
      constraints.push(startAfter(cursor));
    }

    constraints.push(limit(PAGE_SIZE));

    const itemsQuery = query(
      collection(this.firestore, 'users', peechUserId.toLowerCase(), 'items'),
      ...constraints
    );

    return from(getDocs(itemsQuery)).pipe(
      map(snapshot => ({
        items: snapshot.docs.map(doc => this.mapItem(doc.id, doc.data())),
        cursor: snapshot.docs.length === PAGE_SIZE ? snapshot.docs[snapshot.docs.length - 1] : null,
      }))
    );
  }

  // Folder ("tag") contents: fetch exactly these item docs, preserving the GIVEN order —
  // a tag's itemIds array is the source of truth for both membership and position, so this
  // never falls back to the paginated tab query's updatedAt ordering. Chunked 'in' queries
  // over documentId() (30-id Firestore cap), run in parallel; docs that are deleted — or
  // ids that no longer resolve — silently drop out.
  getItemsByIds(peechUserId: string, itemIds: string[]): Observable<LibraryItem[]> {
    if (!itemIds.length) {
      return of([]);
    }

    const itemsCollection = collection(this.firestore, 'users', peechUserId.toLowerCase(), 'items');

    // Tag itemIds arrive lowercased (the iOS wire format), but documentId() matches
    // case-sensitively and item doc ids keep their original casing (see itemDoc). UUID
    // ids only ever exist in two spellings — lowercase (iOS lowercasedString) or
    // uppercase (raw uuidString) — so query both and fold the results back together.
    const candidateIds = Array.from(
      new Set(itemIds.flatMap(id => [id.toLowerCase(), id.toUpperCase()]))
    );

    const chunks: string[][] = [];
    for (let i = 0; i < candidateIds.length; i += IDS_CHUNK_SIZE) {
      chunks.push(candidateIds.slice(i, i + IDS_CHUNK_SIZE));
    }

    return from(
      Promise.all(
        chunks.map(chunk => getDocs(query(itemsCollection, where(documentId(), 'in', chunk))))
      )
    ).pipe(
      map(snapshots => {
        const byId = new Map<string, LibraryItem>();

        for (const snapshot of snapshots) {
          for (const docSnapshot of snapshot.docs) {
            // Same gate as the tab query's where('isDeleted', '==', false): docs missing
            // the flag never match there either, so they are dropped here too.
            if (docSnapshot.data()['isDeleted'] === false) {
              byId.set(
                docSnapshot.id.toLowerCase(),
                this.mapItem(docSnapshot.id, docSnapshot.data())
              );
            }
          }
        }

        return itemIds
          .map(id => byId.get(id.toLowerCase()))
          .filter((item): item is LibraryItem => item !== undefined);
      })
    );
  }

  // Realtime single-item view for the content page: the doc streams status flips
  // (processing → success/failure) while the page is open, the same mechanism iOS uses
  // (FirestoreManager.listenToItemChanges). Emits null when the doc does not exist.
  watchItem(peechUserId: string, itemId: string): Observable<LibraryItem | null> {
    return new Observable<LibraryItem | null>(subscriber => {
      const unsubscribe = onSnapshot(
        this.itemDoc(peechUserId, itemId),
        snapshot => {
          const data = snapshot.data();
          subscriber.next(data ? this.mapItem(snapshot.id, data) : null);
        },
        error => subscriber.error(error)
      );

      return unsubscribe;
    });
  }

  // Soft delete, mirroring the iOS contract (FirestoreLibraryDataSource.softDeleteItems):
  // isDeleted flag + a purge date 30 days out; the actual purge is done by the backend.
  softDeleteItem(peechUserId: string, itemId: string): Observable<void> {
    const purgeAt = Timestamp.fromMillis(Date.now() + 30 * 24 * 60 * 60 * 1000);

    return from(
      updateDoc(this.itemDoc(peechUserId, itemId), {
        isDeleted: true,
        purgeAt,
        updatedAt: serverTimestamp(),
      })
    );
  }

  // iOS restore contract: clear the flag AND the purge date.
  restoreItem(peechUserId: string, itemId: string): Observable<void> {
    return from(
      updateDoc(this.itemDoc(peechUserId, itemId), {
        isDeleted: false,
        purgeAt: null,
        updatedAt: serverTimestamp(),
      })
    );
  }

  // Bulk soft-delete for the library multi-select bar, same fields as softDeleteItem —
  // iOS FirestoreLibraryDataSource.softDeleteItems, one writeBatch per chunk (see
  // WRITE_BATCH_CHUNK_SIZE) instead of one updateDoc per item.
  softDeleteItems(peechUserId: string, itemIds: string[]): Observable<BulkWriteResult> {
    const purgeAt = Timestamp.fromMillis(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return this.batchUpdate(peechUserId, itemIds, { isDeleted: true, purgeAt });
  }

  // Bulk restore, mirroring restoreItem — used by Recently Deleted's multi-select bar.
  restoreItemsBulk(peechUserId: string, itemIds: string[]): Observable<BulkWriteResult> {
    return this.batchUpdate(peechUserId, itemIds, { isDeleted: false, purgeAt: null });
  }

  // Library multi-select bar + the per-row "Move to" action — both are the same write,
  // one item or many. Mirrors iOS FirestoreLibraryDataSource.setLibraryState: the extra
  // fields per target state come from libraryStateTransitionFields (kept as its own
  // exported function so the component can apply the identical patch to its in-memory
  // rows without a refetch).
  setLibraryStateBulk(
    peechUserId: string,
    itemIds: string[],
    state: LibraryState
  ): Observable<BulkWriteResult> {
    return this.batchUpdate(peechUserId, itemIds, libraryStateTransitionFields(state));
  }

  // Chunked writeBatch, mirroring applyBatchUpdate on iOS: every write in every chunk gets
  // the same fields plus a fresh updatedAt (state/delete/restore changes all bump it, unlike
  // the progress-only writes in updateListeningProgress above). Chunks commit sequentially —
  // parallel commits would fan out an unbounded number of in-flight batches for a very large
  // selection. A chunk that fails does not abort the rest, and never rejects: a chunk already
  // committed cannot be rolled back, so the caller gets the exact committed/failed split to
  // patch its rows by instead of an all-or-nothing error that would misreport the first ones.
  private async batchUpdateAsync(
    peechUserId: string,
    itemIds: string[],
    fields: Record<string, unknown>
  ): Promise<BulkWriteResult> {
    const result: BulkWriteResult = { committed: [], failed: [], error: null };

    for (let i = 0; i < itemIds.length; i += WRITE_BATCH_CHUNK_SIZE) {
      const chunk = itemIds.slice(i, i + WRITE_BATCH_CHUNK_SIZE);
      const batch = writeBatch(this.firestore);

      for (const itemId of chunk) {
        batch.update(this.itemDoc(peechUserId, itemId), {
          ...fields,
          updatedAt: serverTimestamp(),
        });
      }

      try {
        await batch.commit();
        result.committed.push(...chunk);
      } catch (error) {
        result.failed.push(...chunk);
        result.error ??= error;
      }
    }

    return result;
  }

  private batchUpdate(
    peechUserId: string,
    itemIds: string[],
    fields: Record<string, unknown>
  ): Observable<BulkWriteResult> {
    if (!itemIds.length) {
      return of({ committed: [], failed: [], error: null });
    }
    return from(this.batchUpdateAsync(peechUserId, itemIds, fields));
  }

  // Recently Deleted, mirroring the iOS query (RecentlyDeletedListViewController): deleted
  // tombstones whose purge date is still ahead — expired ones are the backend's to sweep,
  // and the cutoff is fixed at call time like iOS fixes it at listener-attach time. Newest
  // deletion (latest purgeAt) first, same 25-row pages as the library tabs.
  getDeletedItems(
    peechUserId: string,
    cursor: LibraryCursor | null = null
  ): Observable<LibraryPage> {
    const constraints: QueryConstraint[] = [
      where('isDeleted', '==', true),
      where('purgeAt', '>', Timestamp.now()),
      orderBy('purgeAt', 'desc'),
    ];

    if (cursor) {
      constraints.push(startAfter(cursor));
    }

    constraints.push(limit(PAGE_SIZE));

    const itemsQuery = query(
      collection(this.firestore, 'users', peechUserId.toLowerCase(), 'items'),
      ...constraints
    );

    return from(getDocs(itemsQuery)).pipe(
      map(snapshot => ({
        items: snapshot.docs.map(doc => this.mapItem(doc.id, doc.data())),
        cursor: snapshot.docs.length === PAGE_SIZE ? snapshot.docs[snapshot.docs.length - 1] : null,
      }))
    );
  }

  // Permanent removal is a BACKEND call, never a client-side Firestore doc delete — the
  // server owns tearing down the doc and its stored content. Goes through peech-web-service
  // (peech-services PR #1914), which derives the user from the verified token and forwards
  // to the same gateway route iOS calls directly.
  permanentlyDeleteItems(itemIds: string[]): Observable<void> {
    // Upstream treats an EMPTY itemIds array as "purge everything" — never let a
    // no-selection call fall through to a full wipe.
    if (itemIds.length === 0) {
      return of(undefined);
    }
    return this.deleteRecentlyDeleted(itemIds);
  }

  // iOS purgeAllRecentlyDeleted: the same endpoint with no itemIds purges everything.
  permanentlyDeleteAll(): Observable<void> {
    return this.deleteRecentlyDeleted();
  }

  private deleteRecentlyDeleted(itemIds?: string[]): Observable<void> {
    // No userId in the body: the web service ignores a client-supplied one and resolves
    // the Peech uuid from the interceptor's raw ID token.
    const body: Record<string, unknown> = {};

    if (itemIds) {
      // Verbatim, never lowercased: the backend purge resolves each id with an exact
      // case-sensitive doc lookup and silently SKIPS misses, so lowercasing an
      // uppercase-spelled doc id would strand the item while the UI drops its row.
      body['itemIds'] = itemIds;
    }

    return this.http
      .delete(`${environment.serviceUrl}/items/recently-deleted`, { body })
      .pipe(map(() => undefined));
  }

  renameItem(peechUserId: string, itemId: string, title: string): Observable<void> {
    return from(
      updateDoc(this.itemDoc(peechUserId, itemId), { title, updatedAt: serverTimestamp() })
    );
  }

  // Listening progress, mirroring iOS FirestoreLibraryDataSource.updateItemProgress:
  // strictly monotonic — listeningPartIndex never moves backward and libraryState never
  // downgrades (a replayed earlier part, or a stale player racing another device, must
  // not clobber the furthest position or a completion). A transaction, so the comparison
  // holds against the exact version being written over — two racing clients converge on
  // the maximum. Writes deliberately do NOT bump updatedAt — reordering the library on
  // every played part caused an autoplay loop (IOS-2).
  updateListeningProgress(
    peechUserId: string,
    itemId: string,
    listeningPartIndex: number,
    completed: boolean
  ): Observable<void> {
    const reference = this.itemDoc(peechUserId, itemId);

    return from(
      runTransaction(this.firestore, async transaction => {
        const snapshot = await transaction.get(reference);
        const data = snapshot.data() ?? {};
        const storedIndex =
          typeof data['listeningPartIndex'] === 'number' ? data['listeningPartIndex'] : -1;
        const storedState: LibraryState = data['libraryState'] ?? 'notStarted';

        const update: Record<string, unknown> = {};

        if (listeningPartIndex > storedIndex) {
          update['listeningPartIndex'] = listeningPartIndex;
        }

        if (completed && storedState !== 'completed') {
          update['isFullyRead'] = true;
          update['libraryState'] = 'completed';
        } else if (!completed && storedState === 'notStarted') {
          update['libraryState'] = 'inProgress';
        }

        if (Object.keys(update).length) {
          transaction.update(reference, update);
        }
      })
    ).pipe(map(() => undefined));
  }

  // Reading progress from the reader, mirroring the reader half of iOS
  // FirestoreLibraryDataSource.updateItemProgress: one write carrying only the fields
  // passed. readingItemPartIndex is written AS GIVEN (it is "where you left off", so it may
  // move backwards — unlike listeningPartIndex). libraryState only ever moves forward:
  // isFullyRead promotes to completed, otherwise the requested state applies only when it
  // ranks above the state STORED in the doc — read inside the same transaction, like
  // updateListeningProgress, so a `completed` set by another device between the caller's
  // last snapshot and this write can never be downgraded to inProgress. `currentState` (the
  // caller's last-seen state) only short-circuits a write that could not change anything.
  // Deliberately no updatedAt bump, even on a state change (iOS bumps it there): the web
  // keeps the library sort key out of every progress write. Resolves to the state the doc
  // is in after the write.
  updateReadingProgress(
    peechUserId: string,
    itemId: string,
    progress: ReadingProgressUpdate,
    currentState: LibraryState
  ): Observable<LibraryState> {
    const fields: Record<string, unknown> = {};

    if (progress.readingItemPartIndex !== undefined) {
      fields['readingItemPartIndex'] = progress.readingItemPartIndex;
    }

    if (progress.isFullyRead) {
      fields['isFullyRead'] = true;
    }

    const desiredState = progress.isFullyRead ? 'completed' : progress.libraryState;
    const mayFlipState =
      !!desiredState && LIBRARY_STATE_RANK[desiredState] > LIBRARY_STATE_RANK[currentState];

    if (!Object.keys(fields).length && !mayFlipState) {
      return of(currentState);
    }

    const reference = this.itemDoc(peechUserId, itemId);

    return from(
      // The update object is rebuilt per attempt: Firestore retries the callback on
      // contention, and a libraryState key left over from a previous attempt would leak in.
      runTransaction(this.firestore, async transaction => {
        const snapshot = await transaction.get(reference);
        const storedState: LibraryState = snapshot.data()?.['libraryState'] ?? 'notStarted';
        const update: Record<string, unknown> = { ...fields };
        let resultingState = storedState;

        if (desiredState && LIBRARY_STATE_RANK[desiredState] > LIBRARY_STATE_RANK[storedState]) {
          update['libraryState'] = desiredState;
          resultingState = desiredState;
        }

        if (Object.keys(update).length) {
          transaction.update(reference, update);
        }

        return resultingState;
      })
    );
  }

  // Only the USER id is lowercased (the doc-id contract covers the Peech uuid, not item
  // ids): itemId comes verbatim from doc.id / the route, and lowercasing it would silently
  // target a nonexistent doc if an id ever carried uppercase.
  private itemDoc(peechUserId: string, itemId: string) {
    return doc(this.firestore, 'users', peechUserId.toLowerCase(), 'items', itemId);
  }

  private mapItem(id: string, data: DocumentData): LibraryItem {
    return {
      id,
      status: data['status'] ?? 'processing',
      type: data['type'] ?? 'text',
      title: data['title'] ?? null,
      authors: data['authors'] ?? [],
      createdAt: this.toDate(data['createdAt']),
      updatedAt: this.toDate(data['updatedAt']),
      logoUrl: data['logoUrl'] ?? null,
      durationSeconds: data['durationSeconds'] ?? null,
      partsCount: data['partsCount'] ?? null,
      language: data['language'] ?? null,
      url: data['url'] ?? null,
      domainName: data['domainName'] ?? null,
      contentUrl: data['contentUrl'] ?? null,
      outputType: mapOutputType(data['outputType']),
      tableOfContents: mapTocEntries(data['tableOfContents']),
      listeningPartIndex: data['listeningPartIndex'] ?? null,
      readingItemPartIndex: data['readingItemPartIndex'] ?? null,
      currentVoicePresetId: data['currentVoicePresetId'] ?? null,
      isFullyRead: data['isFullyRead'] ?? false,
      isDeleted: data['isDeleted'] ?? false,
      purgeAt: this.toDate(data['purgeAt']),
      libraryState: data['libraryState'] ?? 'notStarted',
    };
  }

  private toDate(value: unknown): Date | null {
    return value instanceof Timestamp ? value.toDate() : null;
  }
}

// iOS LibraryState.allowedManualTransitions (LibraryState.swift): the states a manual
// "Mark Completed" / "Mark Not Completed" / row "Move to" action may target from each
// current state. Exported so the row menu can list only the valid targets.
export const ALLOWED_LIBRARY_STATE_TRANSITIONS: Record<LibraryState, LibraryState[]> = {
  notStarted: ['completed'],
  inProgress: ['notStarted', 'completed'],
  completed: ['notStarted'],
};

// iOS FirestoreLibraryDataSource.stateTransitionFields: a state change on iOS is never just
// the libraryState field — completing an item also sets isFullyRead, and un-completing one
// (Mark Not Completed / "Move to To Start") resets the progress markers that would otherwise
// strand the item mid-list. `inProgress` (the per-row "Move to Continue" target) carries no
// extra fields. Exported so the component can apply the identical patch to an in-memory row
// after a write succeeds, instead of refetching it.
export function libraryStateTransitionFields(state: LibraryState): Record<string, unknown> {
  switch (state) {
    case 'completed':
      return { libraryState: 'completed', isFullyRead: true };
    case 'notStarted':
      return {
        libraryState: 'notStarted',
        isFullyRead: false,
        readingItemPartIndex: 0,
        listeningPartIndex: 0,
      };
    case 'inProgress':
      return { libraryState: 'inProgress' };
  }
}

const OUTPUT_TYPE_CODES: LibraryOutputTypeCode[] = ['summary', 'youtubeSummary', 'podcast', 'quiz'];

function mapOutputType(raw: unknown): LibraryOutputType | null {
  const code = (raw as { code?: unknown })?.code;
  return OUTPUT_TYPE_CODES.includes(code as LibraryOutputTypeCode)
    ? { code: code as LibraryOutputTypeCode }
    : null;
}

// Tolerant on purpose: the backend owns this field and iOS decodes entries loosely too —
// a malformed entry must not take down the whole item.
function mapTocEntries(raw: unknown): TableOfContentsEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map(entry => ({
      label: typeof entry?.label === 'string' ? entry.label : '',
      partIndex: typeof entry?.partRange?.from === 'number' ? entry.partRange.from : null,
      children: mapTocEntries(entry?.children),
    }))
    .filter(entry => entry.label || entry.children.length);
}
